/**
 * /login: the Log in page (features/site/login/LoginScreen.tsx). Public and
 * outside both the app shell and the site frame, as the handoff draws it
 * (design/website/Login.dc.html); its own code-split chunk, so a visitor
 * never loads the signed-in AppShell.
 *
 * A chef who is already logged in goes straight on: to the gate they still
 * have to clear, else to `next`, else to the dashboard. `next` (a same-site
 * path) and `reason` come from main.tsx onGateError, when the server ended
 * a session.
 *
 * A session check that fails (the API down mid-deploy, a 502, a lost
 * connection) counts as logged out, asked once with no retries: the form
 * still shows, and a log-in sent while the API is down says so itself.
 * Anything else that stops the page loading gets the designed error page
 * (features/public/PublicError), never the router's own.
 */
import { createFileRoute, redirect } from '@tanstack/react-router';
import { PublicError } from '@/features/public/PublicError';
import { LoginScreen } from '@/features/site/login/LoginScreen';
import { isLoginReason, safeNext, type LoginReason } from '@/features/site/login/loginModel';
import { gateRoute, sessionQuery } from '@/lib/session';

type Search = { next?: string; reason?: LoginReason };

export const Route = createFileRoute('/login')({
  validateSearch: (s: Record<string, unknown>): Search => ({
    next: typeof s.next === 'string' ? s.next : undefined,
    reason: isLoginReason(s.reason) ? s.reason : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    const s = await context.queryClient.ensureQueryData({ ...sessionQuery, retry: false }).catch(() => null);
    if (s?.authenticated) throw redirect({ to: gateRoute(s) ?? safeNext(search.next) ?? '/dashboard' });
  },
  errorComponent: PublicError,
  component: Login,
});

function Login() {
  const { next, reason } = Route.useSearch();
  return <LoginScreen next={next} reason={reason} />;
}
